/**
 * API Client with Authentication
 *
 * Axios instance configured with:
 * - Auto token injection
 * - Token refresh on 401
 * - Request/response interceptors
 */

import axios, { AxiosError } from 'axios';

// Use relative URLs to leverage Vite proxy in development
// In production, this will be set via VITE_API_URL env var
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookies (refresh token)
});

// Token storage
let accessToken: string | null = localStorage.getItem('accessToken');

export function setAccessToken(token: string | null) {
  accessToken = token;
  if (token) {
    localStorage.setItem('accessToken', token);
  } else {
    localStorage.removeItem('accessToken');
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

// Request interceptor - add auth token to requests
apiClient.interceptors.request.use(
  (config: any) => {
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle token refresh
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest: any = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh the token
        const response = await axios.post(
          `${API_BASE_URL}/api/auth/refresh`,
          {},
          { withCredentials: true }
        );

        const { accessToken: newToken } = response.data;
        setAccessToken(newToken);

        processQueue(null, newToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Refresh failed, clear tokens and redirect to login
        setAccessToken(null);
        localStorage.removeItem('user');
        window.location.href = '/login';

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// API methods
export const auth = {
  login: async (email: string, password: string) => {
    // Use REST endpoint instead of tRPC to bypass batching issues
    const response = await apiClient.post('/api/simple-login', { email, password });
    // Response format: { user: {...}, accessToken: "..." }
    return {
      success: true,
      ...response.data
    };
  },

  logout: async () => {
    const response = await apiClient.post('/api/auth/logout');
    setAccessToken(null);
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await apiClient.get('/api/auth/me');
    return response.data;
  },

  refresh: async () => {
    const response = await apiClient.post('/api/auth/refresh');
    return response.data;
  },

  // Sliding-window refresh for the simple-auth path: swaps a still-valid 7-day token
  // for a fresh one so an actively-used device never hits the silent expiry wall.
  // Sends the current token via the Authorization header (added by the request
  // interceptor) so it works on mobile even when the cross-site cookie is unavailable.
  refreshSimple: async (): Promise<{ success: boolean; accessToken?: string; user?: any }> => {
    const response = await apiClient.post('/api/simple-refresh');
    return response.data;
  },
};

export default apiClient;
